import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Decimal } from 'decimal.js';
import {
  Input,
  filterNulls,
  nextQuantity,
  shortenQuantity,
} from '@web/utils/src';
import { CartItem } from '@agrantis/cart';

interface Props {
  cartItem: CartItem;
  quantity?: number;
  onAmountChange: (data: { amount: number }) => void;
  noSuggestions?: boolean;
}

const LoosePicker: React.FC<Props> = ({
  cartItem,
  onAmountChange,
  quantity,
  noSuggestions = false,
}) => {
  const { t } = useTranslation();

  const [totalQuantityText, setTotalQuantityText] = useState(
    quantity ?? cartItem?.lineTotalQuantity.toString() ?? '',
  );

  const onQuantityChange = (text: string) => {
    setTotalQuantityText(text);
    const amount = parseFloat(text ?? '0') / (cartItem?.itemData.quantity ?? 1);
    onAmountChange({ amount });
  };

  const suggestions = useMemo(() => {
    if (noSuggestions) return [];
    const deliveryOptions = filterNulls(
      cartItem?.itemData.quantityOptions?.delivery ?? [],
    );
    if (!deliveryOptions) return [];

    const options: Array<{
      label: string;
      totalQuantity: number;
      amount: number;
    }> = [];

    if (deliveryOptions.length) {
      let lastQuantity: number | null = null;
      const quantityDecimal = new Decimal(cartItem?.itemData.quantity ?? 1);
      for (let i = 0; i < 8; i++) {
        lastQuantity = nextQuantity(deliveryOptions, lastQuantity);
        if (lastQuantity) {
          const amountDecimal = new Decimal(lastQuantity).div(quantityDecimal);
          options.push({
            label: `${shortenQuantity(lastQuantity, cartItem?.itemData.unit)}`,
            totalQuantity: lastQuantity,
            amount: amountDecimal.toNumber(),
          });
        } else {
          break;
        }
      }
    }
    return options;
  }, [cartItem]);

  return (
    <div className="flex flex-col">
      {suggestions.length > 0 && (
        <div className="flex pb-0-5 mb-0-5 -mx-0-5 px-0-5">
          {suggestions.map((option) => (
            <button
              key={option.label}
              onClick={() => {
                setTotalQuantityText(option.totalQuantity.toString());
                onAmountChange({ amount: option.amount });
              }}
              className={`
                flex items-center justify-center
                border border-primary-border rounded-5-0
                py-0-2 px-0-5 mr-0-2 min-w-fit
                font-light text-body2
                cursor-pointer
                transition-colors
                ${
                  totalQuantityText == option.totalQuantity
                    ? 'bg-primary-yellow'
                    : 'bg-white hover:bg-gray-50'
                }
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center rounded-lg relative">
        <Input
          value={totalQuantityText}
          onChange={(e, val) => onQuantityChange(val.toString())}
          type="number"
          step="0.01"
          data-testid="quantity_input"
          showClear={false}
        />
        <span className="font-light text-base text-gray-600 absolute text-body1 right-1-0">
          {cartItem?.itemData.unit}
        </span>
      </div>
    </div>
  );
};

export default LoosePicker;
